import React, { useEffect, useRef, useState } from 'react';
import pullAll from 'lodash/pullAll';
import groupBy from 'lodash/groupBy';
import difference from 'lodash/difference';
import { FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { Checkbox } from '../checkbox/index';
export const FormTreeSelect = (props) => {
    const [activeNodes, setActiveNodes] = useState([]);
    const flattenedTreesRef = useRef(new Map());
    const prioritizedSelection = useRef(new Map());
    const prioritizedSelectionEnabled = props.typePriority && props.typePriority.length > 0;
    useEffect(() => {
        if (!prioritizedSelectionEnabled) {
            return;
        }
        const initialPrioritizedSelection = (props.typePriority || []).reduce((acc, curr) => {
            acc[curr] = [];
            return acc;
        }, {});
        props.nodes.forEach(node => {
            prioritizedSelection.current.set(node.value, initialPrioritizedSelection);
            flattenTree(node, null, node.value);
            updateFlattenedTreeSelection(flattenedTreesRef.current.get(node.value) || [], props.selected);
        });
    }, []);
    const flattenTree = (tree, parent, rootNodeId) => {
        const node = {
            value: tree.value,
            label: tree.label,
            type: tree.type,
            selected: tree.selected || false,
            parent: parent ? parent.value : null,
        };
        const nodes = flattenedTreesRef.current.get(rootNodeId) || [];
        flattenedTreesRef.current.set(rootNodeId, [...nodes, node]);
        tree.children &&
            tree.children.forEach(child => flattenTree(child, tree, rootNodeId));
    };
    const updateParentNode = (flattenedTree, currentNodeIdx) => {
        // Find parent node
        const parentNodeIdx = flattenedTree.findIndex(n => n.value === flattenedTree[currentNodeIdx].parent);
        // If there's no parent, we're at root now.
        // We need to find all direct children of the
        // root node and set their selected state properly.
        if (parentNodeIdx === -1) {
            const children = flattenedTree.filter(n => n.parent === flattenedTree[currentNodeIdx].value);
            if (children.every(n => n.selected))
                flattenedTree[currentNodeIdx].selected = true;
            return;
        }
        if (flattenedTree[parentNodeIdx].selected)
            return;
        // Find all nodes with the same parent
        const sameParentNodes = flattenedTree.filter(n => n.parent === flattenedTree[currentNodeIdx].parent);
        if (sameParentNodes.every(n => n.selected)) {
            flattenedTree[parentNodeIdx].selected = true;
        }
        if (flattenedTree[parentNodeIdx].parent !== null) {
            const newIdx = flattenedTree.findIndex(n => n.value === flattenedTree[parentNodeIdx].parent);
            updateParentNode(flattenedTree, newIdx);
        }
    };
    const updateFlattenedTreeSelection = (tree, selectedNodeIds) => {
        // Unselected all items, reset the selection states
        if (selectedNodeIds.length === 0) {
            return tree.map(node => {
                node.selected = false;
                return node;
            });
        }
        tree.forEach(node => (node.selected = selectedNodeIds.indexOf(node.value) > -1));
        for (const nodeId of selectedNodeIds) {
            const nodeIdx = tree.findIndex(n => n.value === nodeId);
            if (nodeIdx === -1)
                continue;
            if (tree[nodeIdx].parent === null)
                continue;
            updateParentNode(tree, nodeIdx);
        }
    };
    const computePrioritizedSelection = (tree) => {
        const typePriority = props.typePriority || [];
        const result = {};
        const groupedByType = groupBy(tree, 'type');
        typePriority.forEach(type => (result[type] = []));
        for (const [idx, type] of typePriority.entries()) {
            result[type] = [];
            const nodes = groupedByType[type];
            // We assumed the first item in the priority list is the most important
            const isHighestPriority = idx === 0;
            // Here, we're assume to have only single root node
            if (isHighestPriority && nodes.some(n => n.selected)) {
                result[type] = nodes.filter(n => n.selected).map(n => n.value);
                break;
            }
            const preceedingPriorityType = typePriority.slice(0, idx).pop();
            result[type] = nodes
                .filter(n => {
                return (n.selected &&
                    !result[preceedingPriorityType].includes(n.parent || ''));
            })
                .map(n => n.value);
        }
        return result;
    };
    const findRootNode = (nodeId) => {
        for (const [rootNodeId, tree] of flattenedTreesRef.current) {
            const node = tree.find(n => n.value === nodeId);
            if (node)
                return rootNodeId;
        }
        return '';
    };
    const isNodeActive = (key) => {
        const existing = activeNodes.indexOf(key);
        return existing !== -1;
    };
    const toggleActive = (key) => {
        const active = [...activeNodes];
        const existing = active.indexOf(key);
        if (existing !== -1) {
            active.splice(existing, 1);
        }
        else {
            active.push(key);
        }
        setActiveNodes(active);
    };
    const toggleSelectValue = (selected, value) => {
        const existing = selected.indexOf(value);
        if (existing === -1)
            selected.push(value);
        else
            selected.splice(existing, 1);
    };
    const nodeGetDeepestChildrenValues = (node) => {
        let children = [];
        if (node.children) {
            for (const child of node.children) {
                if (child.children) {
                    children = [...children, ...nodeGetDeepestChildrenValues(child)];
                }
                else {
                    children.push(child.value);
                }
            }
        }
        return children;
    };
    const onSelect = (node) => {
        const { onChange } = props;
        let selected = [...props.selected];
        if (node.children) {
            const childValues = nodeGetDeepestChildrenValues(node);
            const unselectedChildren = difference(childValues, selected);
            if (unselectedChildren.length > 0) {
                // SOME OR ALL UNSELECTED | SELECT ALL
                unselectedChildren.forEach(v => {
                    if (selected.indexOf(v) === -1)
                        selected.push(v);
                });
            }
            else if (unselectedChildren.length === 0) {
                // ALL SELECTED | REMOVE ALL
                selected = pullAll(selected, childValues);
            }
        }
        else {
            toggleSelectValue(selected, node.value);
        }
        if (prioritizedSelectionEnabled) {
            const rootNodeId = findRootNode(node.value);
            const tree = flattenedTreesRef.current.get(rootNodeId) || [];
            updateFlattenedTreeSelection(tree, selected);
            prioritizedSelection.current.set(rootNodeId, computePrioritizedSelection(tree));
            onChange(selected, Array.from(prioritizedSelection.current).reduce((obj, [key, value]) => Object.assign(obj, { [key]: value }), {}));
            return;
        }
        onChange(selected, {});
    };
    const renderNodes = (nodes, depth, parentIndex) => {
        const { id, selected } = props;
        return (React.createElement("div", { className: depth === 0 ? '' : 'm-l-2' }, nodes.map((node, i) => {
            const key = `${depth}-${parentIndex}-${i}`;
            const hasChildren = !!node.children;
            const isActive = hasChildren ? isNodeActive(key) : false;
            if (depth === 0 &&
                (!node.children || (node.children && node.children.length === 0)))
                return null;
            let checkedPartial = false;
            let checked = false;
            if (!hasChildren) {
                checked = selected.indexOf(node.value) !== -1;
            }
            else {
                const childValues = nodeGetDeepestChildrenValues(node);
                if (childValues.length > 0) {
                    const unselectedChildren = difference(childValues, selected);
                    if (unselectedChildren.length === childValues.length) {
                        // NOTHING SELECTED
                        // checked = true;
                    }
                    else if (unselectedChildren.length > 0) {
                        // SOME SELECTED
                        checkedPartial = true;
                    }
                    else if (unselectedChildren.length === 0) {
                        // ALL SELECTED
                        checked = true;
                    }
                }
            }
            return (React.createElement("div", { key: i, className: depth === 0 && i === 0 ? '' : 'p-t-2' },
                React.createElement("div", { className: "flex-line" },
                    hasChildren && (React.createElement("div", { className: "cursor flex-center", style: { width: '30px' }, onClick: () => toggleActive(key) }, isActive ? React.createElement(FaChevronDown, null) : React.createElement(FaChevronRight, null))),
                    !hasChildren && React.createElement("div", { style: { width: '30px' } }),
                    React.createElement(Checkbox, { id: `tree-${id}-${key}`, name: `tree-${id}-${key}`, checked: checked, checkedPartial: checkedPartial, onChange: () => onSelect(node), label: node.label })),
                isActive &&
                    node.children &&
                    renderNodes(node.children, depth + 1, i)));
        })));
    };
    const { nodes } = props;
    return renderNodes(nodes, 0, 0);
};
//# sourceMappingURL=index.js.map